/**
 * Which days may the stock-in purchase-date picker offer?
 *
 * Extracted from StockInForm's calendar because getting it wrong is silent and expensive:
 * a wrongly disabled day has no onClick, so tapping it does nothing, the popup stays open
 * and the field goes on showing the old date. People then submit believing they
 * back-dated the bill, and the purchase lands on today in every report that filters
 * StockInRecords.received_date — the expense card, the expense-details modal, daily
 * contribution, the EOM report.
 *
 * That is exactly what the old rule did. Its floor was `onboardingDate || today`, and the
 * onboarding date is fetched asynchronously (localStorage, then /auth/status), so it is
 * null before that resolves, null when the cached user blob has no creation date, and null
 * when the call fails. In every one of those cases the floor became TODAY and the calendar
 * could only ever pick today.
 *
 * The two bounds are not symmetric, and that is the whole point:
 *
 *   • The CEILING is always known — you cannot receive stock in the future — so it is
 *     always enforced.
 *   • The FLOOR is only knowable when we know when the business started. Unknown means
 *     "no lower bound", never "today". We do not invent a date to clamp against.
 */

/** Midnight of a date, in local time, without mutating the input. */
function atMidnight(value) {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  d.setHours(0, 0, 0, 0);
  return d;
}

/**
 * @param {Date} cellDate       the calendar day being rendered
 * @param {Date|null} [onboardingDate]  optional/legacy param retained for signature compatibility
 * @param {Date} [now]          injectable for tests; defaults to the real clock
 * @returns {boolean} true when the day must not be selectable
 */
function isStockInDateDisabled(cellDate, onboardingDate = null, now = new Date()) {
  const cell = atMidnight(cellDate);
  const today = atMidnight(now);
  if (!cell || !today) return true; // an unreadable day is never safe to offer

  return cell > today; // no receiving stock in the future; past dates are always allowed
}

export { isStockInDateDisabled, atMidnight };

